import { Card, Tag, Alert, Title, Text, Icon } from '@nimbus-ds/components'
import {
  MobileIcon, DesktopIcon, ExclamationTriangleIcon, InfoCircleIcon,
} from '@nimbus-ds/icons'
import type { ExperimentDeviceAnalysis, ExperimentDeviceSegment, BBVerdict } from '@/types'

interface DevicePerformanceCardProps {
  analysis: ExperimentDeviceAnalysis
  isNegative: boolean
  /** Metric the per-device rates count (e.g. "tasa de compra") — device cells are purchases, not the goal. */
  metricLabel?: string
}

// Below this many sessions in a segment an SRM flag is noise (the exact-binomial test is tuned to
// flag at α=0.05 and fires on most small segments). Surface it as a caption, not a red alert.
const SRM_ALERT_MIN_SESSIONS = 200

const DEVICE_META: Record<string, { label: string; Icon: typeof MobileIcon }> = {
  mobile: { label: 'Mobile', Icon: MobileIcon },
  desktop: { label: 'Desktop', Icon: DesktopIcon },
}

// Frame a per-segment BB verdict from the hypothesis's perspective (matches DecisionHelper + the
// page's comparison table: for a negative experiment "control" is the hypothesis under test).
function verdictChip(verdict: BBVerdict, isNegative: boolean) {
  if (verdict === 'inconclusive') {
    return <Tag appearance="neutral">Sin diferencia concluyente</Tag>
  }
  const treatmentWon = verdict === 'treatment_better'
  const hypothesisWon = isNegative ? !treatmentWon : treatmentWon
  return hypothesisWon
    ? <Tag appearance="success">Hipótesis mejor</Tag>
    : <Tag appearance="warning">Base mejor</Tag>
}

export function DevicePerformanceCard({ analysis, isNegative, metricLabel }: DevicePerformanceCardProps) {
  if (!analysis?.present) return null

  const hypKey: 'control' | 'treatment' = isNegative ? 'control' : 'treatment'
  const baseKey: 'control' | 'treatment' = isNegative ? 'treatment' : 'control'
  // Same vocabulary as the Rendimiento table: variant name + role.
  const baseLabel = isNegative ? 'Tratamiento (línea base)' : 'Control (línea base)'
  const hypLabel = isNegative ? 'Control (hipótesis)' : 'Tratamiento (hipótesis)'
  const order: Array<'mobile' | 'desktop'> = ['mobile', 'desktop']
  const segments = order
    .map((key) => ({ key, seg: analysis.segments[key] }))
    .filter((s): s is { key: 'mobile' | 'desktop'; seg: ExperimentDeviceSegment } =>
      s.seg != null && (s.seg.control.sessions + s.seg.treatment.sessions) > 0)

  if (segments.length === 0) return null

  const segSessions = (seg: ExperimentDeviceSegment) => seg.control.sessions + seg.treatment.sessions
  // Red alert only for a flagged segment with enough traffic for the flag to mean something.
  const srmAlertSegments = segments.filter((s) => s.seg.srm.flagged && segSessions(s.seg) >= SRM_ALERT_MIN_SESSIONS)
  const srmSmallSegments = segments.filter((s) => s.seg.srm.flagged && segSessions(s.seg) < SRM_ALERT_MIN_SESSIONS)
  const { simpson, coverage } = analysis
  const resolvedMetricLabel = metricLabel ?? (analysis.metric === 'purchase' ? 'tasa de compra' : undefined)

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between gap-2">
          <Title as="h4">Rendimiento por dispositivo</Title>
          {resolvedMetricLabel && <Text fontSize="caption">{resolvedMetricLabel}</Text>}
        </div>
      </Card.Header>
      <Card.Body>
        <div className="space-y-4">
          {/* Simpson's: the most important per-device signal — surfaced first. */}
          {simpson?.detected && simpson.status === 'confounded' && (
            <Alert title="Agregado no confiable (mezcla de dispositivos)" appearance="warning">
              {simpson.message}
            </Alert>
          )}
          {simpson?.detected && simpson.status === 'heterogeneous' && (
            <Alert title="El efecto difiere por dispositivo" appearance="primary">
              {simpson.message}
            </Alert>
          )}

          {/* SRM-by-device: the assignment mechanism is skewed on at least one device. */}
          {srmAlertSegments.length > 0 && (
            <Alert title="Distribución de tráfico sesgada (SRM por dispositivo)" appearance="danger">
              El reparto de sesiones entre variantes no coincide con el diseño en{' '}
              {srmAlertSegments.map((s) => DEVICE_META[s.key].label).join(' y ')}.
              No tomes decisiones sobre ese segmento hasta revisar la asignación.
            </Alert>
          )}

          {/* Per-device rows */}
          <div className="space-y-3">
            {segments.map(({ key, seg }) => {
              const { Icon: DeviceIcon, label } = DEVICE_META[key]
              const baseCR = seg[baseKey].conversion_rate * 100
              const hypCR = seg[hypKey].conversion_rate * 100
              const baseSessions = seg[baseKey].sessions
              const hypSessions = seg[hypKey].sessions
              return (
                <div key={key} className="rounded-lg border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Icon source={<DeviceIcon />} />
                      <Text color="neutral-textHigh">{label}</Text>
                      {seg.role === 'confirmatory'
                        ? <Tag appearance="neutral">Principal</Tag>
                        : <Tag appearance="neutral">Exploratorio</Tag>}
                      {seg.srm.flagged && segSessions(seg) >= SRM_ALERT_MIN_SESSIONS && (
                        <span title="SRM: distribución sesgada en este dispositivo" className="text-red-500">
                          <Icon source={<ExclamationTriangleIcon />} color="currentColor" />
                        </span>
                      )}
                    </div>
                    <div className="shrink-0 whitespace-nowrap">{verdictChip(seg.bb_rope.verdict, isNegative)}</div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    <div>
                      <Text fontSize="caption">{baseLabel}</Text>
                      <Text fontWeight="bold" color="neutral-textHigh" className="tabular-nums">{baseCR.toFixed(1)}%</Text>
                      <Text fontSize="caption">{baseSessions.toLocaleString('es-ES')} sesiones</Text>
                    </div>
                    <div>
                      <Text fontSize="caption">{hypLabel}</Text>
                      <Text fontWeight="bold" color="neutral-textHigh" className="tabular-nums">{hypCR.toFixed(1)}%</Text>
                      <Text fontSize="caption">{hypSessions.toLocaleString('es-ES')} sesiones</Text>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Coverage guard: a large unclassified share makes the per-device read low-confidence (§8.5). */}
          {coverage?.low_confidence && (
            <Text fontSize="caption" className="flex items-start gap-1.5">
              <Icon source={<InfoCircleIcon />} />
              {(coverage.unknown_share * 100).toFixed(0)}% de las sesiones no tienen dispositivo
              identificable, así que la lectura por dispositivo es de baja confianza (se evalúa solo el
              tráfico clasificable).
            </Text>
          )}

          {/* Small-segment SRM: mention it, don't alarm — at this N the test flags routinely. */}
          {srmSmallSegments.length > 0 && (
            <Text fontSize="caption">
              Reparto algo desparejo en {srmSmallSegments.map((s) => DEVICE_META[s.key].label).join(' y ')} — con
              menos de {SRM_ALERT_MIN_SESSIONS} sesiones es esperable; se revisa si persiste al crecer la muestra.
            </Text>
          )}

          {/* Exploratory caveat — only the primary segment is a ship-decision basis (§8.3). */}
          {segments.some((s) => s.seg.role === 'exploratory') && (
            <Text fontSize="caption">
              Solo el segmento <strong>principal</strong> es base de decisión. Los segmentos exploratorios
              son orientativos (no se pre-registraron como confirmatorios).
            </Text>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}

export default DevicePerformanceCard
